// src/api/responsesApi.js
import apiClient from "./apiClient";

const responsesApi = {
  // 📌 Guardar respuestas y generar interpretación
  createHRAResponse: async (responseData) => {
    try {
      const response = await apiClient.post("/hra/responses", responseData);
      return response.data;
    } catch (error) {
      console.error("Error guardando HRA response:", error);
      throw error;
    }
  },

  // 📌 Eliminar una HRA response por su ID
  deleteHRAResponse: async (responseId) => {
    try {
      const response = await apiClient.delete(`/hra/responses/${responseId}`);
      return response.data;
    } catch (error) {
      console.error("Error eliminando HRA response:", error);
      throw error;
    }
  },

  // 📌 Exportar HRA responses a Excel
  //    Si companyId es undefined, devuelve todas; si viene string, filtra por esa compañía.
  exportHRAResponses: async (companyId) => {
    try {
      const config = {
        responseType: "blob",
        params: {}
      };
      if (companyId) {
        config.params.companyId = companyId;
      }
      const response = await apiClient.get("/hra/responses/reports", config);
      // El backend envía un archivo .xlsx; aquí devolvemos el blob para que el front lo descargue.
      return response.data;
    } catch (error) {
      console.error("Error exportando HRA responses:", error);
      throw error;
    }
  },

  // 📌 Guardar respuestas de burnout y generar interpretación
  createEstresResponse: async (responseData) => {
    try {
      const response = await apiClient.post("/burnout/responses", responseData);
      return response.data;
    } catch (error) {
      console.error("Error guardando estrés response:", error);
      throw error;
    }
  },

  // 📌 Eliminar una estrés response por su ID
  deleteEstresResponse: async (responseId) => {
    try {
      const response = await apiClient.delete(`/burnout/responses/${responseId}`);
      return response.data;
    } catch (error) {
      console.error("Error eliminando estrés response:", error);
      throw error;
    }
  },

  // 📌 Exportar estrés & burnout responses a Excel
  //    Si companyId es undefined, devuelve todas; si viene string, filtra por esa compañía.
  exportEstresResponses: async (companyId) => {
    try {
      const config = {
        responseType: "blob",
        params: {}
      };
      if (companyId) {
        config.params.companyId = companyId;
      }
      const response = await apiClient.get("/burnout/responses/reports", config);
      // El backend envía un archivo .xlsx; aquí devolvemos el blob para que el front lo descargue.
      return response.data;
    } catch (error) {
      console.error("Error exportando estrés responses:", error);
      throw error;
    }
  },

  // 📌 Guardar respuestas de HRA & Burnout (fusionado)
  createHBResponse: async (responseData) => {
    try {
      const response = await apiClient.post("/hb/responses", responseData);
      return response.data;
    } catch (error) {
      console.error("Error guardando HB response:", error);
      throw error;
    }
  },

  // 📌 Eliminar una respuesta HB
  deleteHBResponse: async (responseId) => {
    try {
      const response = await apiClient.delete(`/hb/responses/${responseId}`);
      return response.data;
    } catch (error) {
      console.error("Error eliminando HB response:", error);
      throw error;
    }
  },

  // 📌 Exportar respuestas HB
  exportHBResponses: async (companyId) => {
    try {
      const config = {
        responseType: "blob",
        params: {}
      };
      if (companyId) {
        config.params.companyId = companyId;
      }
      const response = await apiClient.get("/hb/responses/reports", config);
      return response.data;
    } catch (error) {
      console.error("Error exportando HB responses:", error);
      throw error;
    }
  },

  // 📌 Guardar respuestas de NOM-035 y generar interpretación
  createNom035Response: async (responseData) => {
    try {
      const response = await apiClient.post("/nom035/responses", responseData);
      return response.data;
    } catch (error) {
      console.error("Error guardando NOM-035 response:", error);
      throw error;
    }
  },

  // 📌 Guardar respuestas de NOM-035 Sec I y generar interpretación
  createNom035SecIResponse: async (responseData) => {
    try {
      const response = await apiClient.post("/nom035SecI/responses", responseData);
      return response.data;
    } catch (error) {
      console.error("Error guardando NOM-035 Sec I response:", error);
      throw error;
    }
  },

  // Guardar respuestas de GHQ
  createGhqResponse: async (responseData) => {
    try {
      const response = await apiClient.post("/ghq/responses", responseData);
      return response.data;
    } catch (error) {
      console.error("Error guardando GHQ response:", error);
      throw error;
    }
  },

  // Compatibilidad con nombre anterior
  createCgsResponse: async (responseData) => responsesApi.createGhqResponse(responseData),

  // 📌 Exportar respuestas GHQ (General Health)
  //    Si companyId es undefined, devuelve todas; si viene string, filtra por esa compañía.
  exportGhqResponses: async (companyId) => {
    try {
      const config = {
        responseType: "blob",
        params: {}
      };
      if (companyId) {
        config.params.companyId = companyId;
      }
      const response = await apiClient.get("/ghq/responses/reports", config);
      return response.data;
    } catch (error) {
      console.error("Error exportando GHQ responses:", error);
      throw error;
    }
  },

  // 📌 Eliminar una NOM-035 response por su ID
  deleteNom035Response: async (responseId) => {
    try {
      const response = await apiClient.delete(`/nom035/responses/${responseId}`);
      return response.data;
    } catch (error) {
      console.error("Error eliminando NOM-035 response:", error);
      throw error;
    }
  },

  // 📌 Exportar NOM-035 responses a Excel
  //    Si companyId es undefined, devuelve todas; si viene string, filtra por esa compañía.
  exportNom035Responses: async (companyId) => {
    if (!companyId) {
      throw new Error("companyId es requerido para exportar el reporte NOM-035.");
    }

    try {
      const response = await apiClient.get(
        `/nom035/responses/reports/${companyId}`,
        { responseType: "blob" }
      );
      return response.data; // Blob
    } catch (error) {
      console.error("Error exportando NOM-035 responses:", error);
      throw error;
    }
  },

  // 📌 Exportar NOM-035 Sec I responses a Excel
  exportNom035SecIResponses: async (companyId) => {
    if (!companyId) {
      throw new Error("companyId es requerido para exportar el reporte NOM-035 Sec I.");
    }

    try {
      const response = await apiClient.get(
        `/nom035SecI/responses/reports/${companyId}`,
        { responseType: "blob" }
      );
      return response.data; // Blob
    } catch (error) {
      console.error("Error exportando NOM-035 Sec I responses:", error);
      throw error;
    }
  }
};

export default responsesApi;
